/**
 * useSubscription — Layer 3 query hook for the subscription mirror.
 *
 * Pass { poll: true } on the success page: it refetches every POLL_INTERVAL_MS
 * until the webhook flips status to active/past_due, or the attempt cap is hit
 * (nextPollInterval owns that decision; dataUpdateCount is the attempt proxy).
 * Pass { enabled: false } to skip the fetch entirely (e.g. guests).
 */
import { useQuery } from "@tanstack/react-query";
import { subscriptionQueryOptions } from "../api/billingQueryOptions";
import { nextPollInterval } from "../lib/billingHelpers";

export function useSubscription(options: { poll?: boolean; enabled?: boolean } = {}) {
  const { poll = false, enabled } = options;
  return useQuery({
    ...subscriptionQueryOptions,
    // Conditional spread keeps exactOptionalPropertyTypes happy (no `enabled: undefined`).
    ...(enabled === undefined ? {} : { enabled }),
    // dataUpdateCount lives on the Query *instance* (query.state) — it is the
    // attempt counter that stops the poll. It is NOT on the hook return.
    refetchInterval: poll
      ? (query) =>
          nextPollInterval({
            status: query.state.data?.subscription?.status,
            attempts: query.state.dataUpdateCount,
          })
      : undefined,
  });
}
